// Imaging Display Mapping Utility
// Maps standardized parameter names to user-friendly display labels

import { imagingParameters } from './imagingParameters';

/**
 * Creates a mapping from standardized parameter names to display labels
 */
const createImagingDisplayMapping = () => {
  const standardizedToDisplayMap = {};
  const categoryMapping = {};
  const modalityMapping = {};
  const oldToStandardizedMap = {};
  
  Object.entries(imagingParameters).forEach(([studyType, parameters]) => {
    // Extract modality from study type (e.g., "Spleen US" -> "us", "Chest XR" -> "xr")
    const modality = extractModalityFromStudyType(studyType);
    
    parameters.forEach(param => {
      // The parameter key should already be standardized (e.g., imaging_spleen_us_craniocaudal_length)
      const standardizedKey = param.key;
      
      // Extract the parameter name from the standardized key for old mapping
      // e.g., 'imaging_spleen_us_craniocaudal_length' -> 'craniocaudal_length'
      const parts = standardizedKey.split('_');
      const oldKey = parts.slice(3).join('_'); // Remove 'imaging_', modality, and organ parts
      
      // Map standardized key to display label
      standardizedToDisplayMap[standardizedKey] = param.name;
      categoryMapping[standardizedKey] = studyType;
      modalityMapping[standardizedKey] = modality;
      
      // Map old key to standardized key for transformation
      oldToStandardizedMap[oldKey] = standardizedKey;
    });
  });
  
  return {
    standardizedToDisplayMap,
    categoryMapping,
    modalityMapping,
    oldToStandardizedMap
  };
};

/**
 * Extracts modality from study type string
 * @param {string} studyType - Study type (e.g., "Spleen US", "Chest XR")
 * @returns {string} - Modality (e.g., "us", "xr", "ct", "mri")
 */
const extractModalityFromStudyType = (studyType) => {
  const lowerStudyType = studyType.toLowerCase();
  if (lowerStudyType.includes(' us')) return 'us';
  if (lowerStudyType.includes(' xr') || lowerStudyType.includes('x-ray')) return 'xr';
  if (lowerStudyType.includes(' ct')) return 'ct';
  if (lowerStudyType.includes(' mri')) return 'mri';
  if (lowerStudyType.includes('ekg') || lowerStudyType.includes('ecg')) return 'ekg';
  if (lowerStudyType.includes('nuclear')) return 'nuclear';
  return 'unknown';
};

const { standardizedToDisplayMap, categoryMapping, modalityMapping, oldToStandardizedMap } = createImagingDisplayMapping();

/**
 * Gets the display label for a standardized imaging parameter name
 * @param {string} standardizedKey - Standardized parameter key
 * @returns {string} - Human-readable display label
 */
export const getImagingDisplayLabel = (standardizedKey) => {
  return standardizedToDisplayMap[standardizedKey] || standardizedKey;
};

/**
 * Gets the study type for a standardized imaging parameter name
 * @param {string} standardizedKey - Standardized parameter key
 * @returns {string} - Study type name
 */
export const getImagingParameterStudyType = (standardizedKey) => {
  return categoryMapping[standardizedKey] || 'Unknown';
};

/**
 * Gets the modality for a standardized imaging parameter name
 * @param {string} standardizedKey - Standardized parameter key
 * @returns {string} - Modality name
 */
export const getImagingParameterModality = (standardizedKey) => {
  return modalityMapping[standardizedKey] || 'Unknown';
};

/**
 * Transforms flowsheet data for display by mapping parameter names to labels
 * @param {Array} flowsheetData - Array of flowsheet entries
 * @returns {Array} - Transformed data with display labels
 */
export const transformImagingFlowsheetDataForDisplay = (flowsheetData) => {
  if (!Array.isArray(flowsheetData)) {
    return flowsheetData;
  }
  
  return flowsheetData.map(entry => {
    if (entry.type === 'ImagingDetail') {
      // Check if this is an imaging parameter
      if (entry.name && entry.name.startsWith('imaging_')) {
        return {
          ...entry,
          displayName: getImagingDisplayLabel(entry.name),
          studyType: getImagingParameterStudyType(entry.name),
          modality: getImagingParameterModality(entry.name)
        };
      }
    }
    return entry;
  });
};

/**
 * Gets all available imaging parameters with their display information
 * @returns {Array} - Array of parameter objects with key, label, studyType, and modality
 */
export const getAllImagingParameters = () => {
  const parameters = [];
  
  Object.entries(imagingParameters).forEach(([studyType, params]) => {
    const modality = extractModalityFromStudyType(studyType);
    
    params.forEach(param => {
      parameters.push({
        key: param.key,
        label: param.name,
        studyType: studyType,
        modality: modality,
        inputType: param.inputType,
        options: param.options,
        normalOption: param.normalOption,
        unit: param.unit
      });
    });
  });
  
  return parameters;
};

/**
 * Transforms form data from old naming convention to standardized naming
 * @param {Object} formData - Raw form data with old parameter names
 * @returns {Object} - Transformed data with standardized parameter names
 */
export const transformImagingFormDataToStandardized = (formData) => {
  if (!formData || typeof formData !== 'object') {
    return formData;
  }
  
  const transformedData = {};
  
  Object.entries(formData).forEach(([key, value]) => {
    // Check if this is an old parameter name that needs transformation
    const standardizedKey = oldToStandardizedMap[key] || key;
    transformedData[standardizedKey] = value;
  });
  
  return transformedData;
};

/**
 * Gets imaging parameters by study type
 * @param {string} studyType - Study type (e.g., 'Spleen US', 'Chest XR')
 * @returns {Array} - Array of parameters for the study type
 */
export const getImagingParametersByStudyType = (studyType) => {
  const parameters = imagingParameters[studyType];
  return parameters ? parameters.map(param => ({
    key: param.key,
    label: param.name,
    inputType: param.inputType,
    options: param.options,
    normalOption: param.normalOption,
    unit: param.unit
  })) : [];
};

/**
 * Gets imaging parameters by modality
 * @param {string} modality - Modality (e.g., 'us', 'xr', 'ct', 'mri')
 * @returns {Array} - Array of parameters for the modality
 */
export const getImagingParametersByModality = (modality) => {
  const parameters = [];
  
  Object.entries(modalityMapping).forEach(([paramKey, paramModality]) => {
    if (paramModality === modality) {
      parameters.push({
        key: paramKey,
        label: getImagingDisplayLabel(paramKey),
        studyType: getImagingParameterStudyType(paramKey),
        modality: modality
      });
    }
  });
  
  return parameters;
};

/**
 * Gets all available imaging study types
 * @returns {Array} - Array of study type strings
 */
export const getAllImagingStudyTypes = () => {
  return Object.keys(imagingParameters);
};

/**
 * Gets all available imaging modalities
 * @returns {Array} - Array of unique modality strings
 */
export const getAllImagingModalities = () => {
  const modalities = Object.values(modalityMapping);
  return [...new Set(modalities)];
};

/**
 * Checks if a parameter key is a standardized imaging parameter
 * @param {string} parameterKey - Parameter key to check
 * @returns {boolean} - True if it's a standardized imaging parameter
 */
export const isImagingParameter = (parameterKey) => {
  return typeof parameterKey === 'string' &&
         parameterKey.startsWith('imaging_') &&
         standardizedToDisplayMap.hasOwnProperty(parameterKey);
};

/**
 * Validates if a parameter name follows the standardized imaging naming convention
 * @param {string} parameterKey - Parameter key to validate
 * @returns {boolean} - True if follows convention, false otherwise
 */
export const isStandardizedImagingParameter = (parameterKey) => {
  if (!parameterKey || typeof parameterKey !== 'string') {
    return false;
  }

  // Check if it follows the pattern: imaging_<organ>_<modality>_<parameter_name>
  const parts = parameterKey.split('_');
  return parts.length >= 4 && parts[0] === 'imaging';
};

/**
 * Groups imaging parameters by study type for organized display
 * @returns {Object} - Object with study types as keys and parameter arrays as values
 */
export const getImagingParametersGroupedByStudyType = () => {
  const grouped = {};
  
  Object.entries(imagingParameters).forEach(([studyType, params]) => {
    grouped[studyType] = params.map(param => ({
      key: param.key,
      label: param.name,
      inputType: param.inputType,
      options: param.options,
      normalOption: param.normalOption,
      unit: param.unit
    }));
  });
  
  return grouped;
};

/**
 * Groups imaging parameters by modality for organized display
 * @returns {Object} - Object with modalities as keys and parameter arrays as values
 */
export const getImagingParametersGroupedByModality = () => {
  const grouped = {};
  
  Object.entries(modalityMapping).forEach(([paramKey, modality]) => {
    if (!grouped[modality]) {
      grouped[modality] = [];
    }
    
    grouped[modality].push({
      key: paramKey,
      label: getImagingDisplayLabel(paramKey),
      studyType: getImagingParameterStudyType(paramKey)
    });
  });
  
  return grouped;
};

export default {
  getImagingDisplayLabel,
  getImagingParameterStudyType,
  getImagingParameterModality,
  transformImagingFlowsheetDataForDisplay,
  getAllImagingParameters,
  transformImagingFormDataToStandardized,
  getImagingParametersByStudyType,
  getImagingParametersByModality,
  getAllImagingStudyTypes,
  getAllImagingModalities,
  isStandardizedImagingParameter,
  getImagingParametersGroupedByStudyType,
  getImagingParametersGroupedByModality
};
